const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000;

const api = {
  async request(endpoint, options = {}) {
    const token = localStorage.getItem('token');

    // Create timeout abort controller if no signal provided
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT);

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      // Use provided signal or timeout signal
      signal: options.signal || timeoutController.signal,
    };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      clearTimeout(timeoutId);

      // Handle 401/403
      if (response.status === 401 || response.status === 403) {
        const data = await response.json().catch(() => ({}));
        // Only redirect if it's a token issue, not a login/verification issue
        if (response.status === 401 && !endpoint.includes('/auth/login')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        // For login 403 with requiresVerification, throw error with additional info
        const error = new Error(data.error || 'Unauthorized');
        if (data.requiresVerification) {
          error.requiresVerification = true;
          error.email = data.email;
        }
        throw error;
      }

      // Handle blob responses (e.g. CSV exports)
      if (options.responseType === 'blob') {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Request failed');
        }
        const blob = await response.blob();
        return { data: blob };
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      return { data };
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort errors gracefully
      if (error.name === 'AbortError') {
        throw new Error('Request was cancelled');
      }

      throw error;
    }
  },

  get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  },

  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    });
  },

  put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...options,
    });
  },

  delete(endpoint, body, options = {}) {
    const config = { method: 'DELETE', ...options };
    if (body !== undefined) {
      config.body = JSON.stringify(body);
    }
    return this.request(endpoint, config);
  },

  patch(endpoint, body, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
      ...options,
    });
  },
};

export default api;
